import { CallIcon, SocialIcon } from '@/Components/Common/Icons';
import { FIFTEEN_MINUTES_MS } from '@/constants/device';
import useSchedule from '@/Components/Hooks/useSchedule';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { formatEventsForDisplay } from '../../utils';
import './ScheduleCard.less';
import { Tooltip } from 'antd';
import { useRegisterReportSection } from '../../reportUtils';

const ScheduleCard = ({ useruuid, userTimezone, locale, is24HourFormat }) => {
  const trans = useSelector(translator);

  const currentDate = useMemo(() => {
    if (!userTimezone) return null;
    return dayjs().tz(userTimezone);
  }, [userTimezone]);

  const currentSiteIconZip = (state) => state.data.siteIconFiles;
  const currentSiteIconManifest = (state) => state.data.siteIconManifest;
  const siteIconZip = useSelector(currentSiteIconZip);
  const siteIconManifest = useSelector(currentSiteIconManifest);

  const [selectedDate, setSelectedDate] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [formattedEvents, setFormattedEvents] = useState([]);

  useEffect(() => {
    if (currentDate && !selectedDate) {
      setSelectedDate(currentDate);
    }
  }, [currentDate, selectedDate]);

  const [fetchingScheduleList, scheduleList, fetchSchedule] = useSchedule('all', userTimezone);

  useEffect(() => {
    if (!selectedDate || !userTimezone) return;
    fetchSchedule(useruuid, selectedDate, 0, false, 1);
    const id = setInterval(
      () => fetchSchedule(useruuid, selectedDate, 0, true, 1),
      FIFTEEN_MINUTES_MS
    );
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, userTimezone, useruuid]);

  useEffect(() => {
    async function buildCategoryOptions() {
      if (!siteIconZip || !siteIconManifest || typeof siteIconZip.file !== 'function') {
        console.error('Invalid Zip file.');
        return;
      }
      const options = [];
      for (const mainCat of Object.keys(siteIconManifest)) {
        const subcats = siteIconManifest[mainCat];
        for (const subcat of Object.keys(subcats)) {
          const fileName = subcats[subcat];
          const file = siteIconZip.file(fileName);
          if (!file) {
            console.error(`File ${fileName} not found in zip for subcategory "${subcat}"`);
            continue;
          }
          try {
            const blob = await file.async('blob');
            let blobToUse = blob;
            if (!blob.type || blob.type === '') {
              blobToUse = new Blob([blob], { type: 'image/svg+xml' });
            }
            const url = URL.createObjectURL(blobToUse);
            options.push({
              value: `${subcat}`,
              icon: (
                <img
                  src={url}
                  alt={subcat}
                  style={{
                    width: 30,
                    height: 36,
                    verticalAlign: 'middle',
                  }}
                />
              ),
            });
          } catch (error) {
            console.error(`Error loading file ${fileName} for subcategory "${subcat}":`, error);
          }
        }
      }
      options.sort((a, b) => a.value.localeCompare(b.value));
      setCategoryOptions(options);
    }
    buildCategoryOptions();
  }, [siteIconZip, siteIconManifest]);

  useEffect(() => {
    const formattedEvents = formatEventsForDisplay(
      scheduleList,
      userTimezone,
      locale,
      is24HourFormat,
      trans
    );
    setFormattedEvents(formattedEvents);
  }, [scheduleList, userTimezone, locale, is24HourFormat, trans]);

  const scheduleReportRows = useMemo(
    () =>
      (formattedEvents || []).map((item) => ({
        title: item.title,
        time: item.time,
        category: item.category,
      })),
    [formattedEvents]
  );
  useRegisterReportSection('schedule', scheduleReportRows, [scheduleReportRows]);

  const renderCategoryIcon = (type, taskCategory) => {
    if (type === 'schedule' || type === 'zoom') {
      return <CallIcon className="inactive-call-icon" />;
    } else if (type === 'tasks') {
      const option = categoryOptions.find((opt) => opt.value === taskCategory);
      return option ? option.icon : '';
    } else if (type === 'reminder') {
      return <SocialIcon className="event-type-icon event-icon-color" />;
    }
  };

  if (fetchingScheduleList && isEmpty(scheduleList)) {
    return (
      <div className="schedule-skeleton">
        <div className="skeleton-block schedule-skeleton-title" />
        <div className="skeleton-block schedule-skeleton-time" />
        <div className="schedule-skeleton-category">
          <div className="skeleton-block schedule-skeleton-icon" />
          <div className="skeleton-block schedule-skeleton-label" />
        </div>
      </div>
    );
  }

  if ((isEmpty(scheduleList) || isEmpty(formattedEvents)) && !fetchingScheduleList) {
    return (
      <div className="schedule-card-container">
        <div className="schedule-card">
          <span className="schedule-free">{trans('FREE_TIME')}</span>
          <span className="no-events">{trans('NO_EVENTS_SCHEDULED')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="schedule-card-container">
      {formattedEvents.map((item, index) => (
        <div
          className={`schedule-card ${item.title === trans('NOW') ? 'now-indicator' : ''}`}
          key={index}
        >
          <div className="schedule-title">{item.title}</div>
          <span className="schedule-time">{item.time}</span>
          <Tooltip title={item.category} placement="bottom">
            <div className="schedule-category">
              {renderCategoryIcon(item.eventType, item.taskCategory)}
              <span className="category">{item.category}</span>
            </div>
          </Tooltip>
        </div>
      ))}
    </div>
  );
};

ScheduleCard.propTypes = {
  useruuid: PropTypes.string,
  userTimezone: PropTypes.string,
  locale: PropTypes.string,
  is24HourFormat: PropTypes.bool,
};

export default ScheduleCard;
